/**
 * figure HTML에서 figcaption과 table을 추출하는 함수
 * - 최상위 figure 태그의 직접 자식만 검사
 * - 중첩된 figure 태그 처리
 * - figurecaption 오타도 figcaption으로 취급
 * - table 은 한 figure 안에 여러 개가 올 수 있으므로 문서 순서대로 모두 반환
 */

// figcaption 태그인지 확인 (오타 포함)
const isFigcaptionTag = (tag: string) =>
  tag === "figcaption" || tag === "figurecaption";

// table 태그인지 확인
const isTableTag = (tag: string) => tag === "table";

// 요소의 태그 이름을 소문자로 반환
const getTagName = (element: Element) => element.tagName.toLowerCase();

// 최상위 figure의 자식들을 평탄화 (중첩 figure 처리)
const flattenFigureChildren = (figure: Element): Element[] =>
  Array.from(figure.children).flatMap(child =>
    getTagName(child) === "figure" ? Array.from(child.children) : [child],
  );

// 특정 조건의 첫 번째 요소 찾기
const findFirstByTag = (
  elements: Element[],
  predicate: (tag: string) => boolean,
): Element | undefined => elements.find(el => predicate(getTagName(el)));

// 특정 조건의 요소를 문서 순서대로 모두 찾기
const findAllByTag = (
  elements: Element[],
  predicate: (tag: string) => boolean,
): Element[] => elements.filter(el => predicate(getTagName(el)));

export function extractFigureContent(figureString: string): {
  figcaption: string;
  tables: string[];
} {
  const parser = new DOMParser();
  const doc = parser.parseFromString(figureString, "text/html");

  const topLevelFigure = doc.querySelector("figure");
  if (!topLevelFigure) {
    return { figcaption: "", tables: [] };
  }

  // children 은 직접 자식만 담으므로, 다른 table 의 셀 안에 중첩된 table 은
  // 여기에 잡히지 않는다 (부모 table 의 outerHTML 에 이미 포함되어 있다).
  const children = flattenFigureChildren(topLevelFigure);
  const figcaptionElement = findFirstByTag(children, isFigcaptionTag);
  const tableElements = findAllByTag(children, isTableTag);

  return {
    figcaption: figcaptionElement
      ? `<figcaption>${figcaptionElement.innerHTML}</figcaption>`
      : "",
    tables: tableElements.map(el => el.outerHTML),
  };
}
